import moment from "moment";
import "moment/locale/es";
moment.locale("es");

const MODO_LABELS   = { 1: "Ciego", 2: "Semi-ciego", 3: "Abierto" };
const ESTRATEGIA_LABELS = {
    1: "Más rotación", 2: "Menos rotación",
    3: "Aleatorio (existentes)", 4: "Aleatorio (todos)",
    5: "Todos", 6: "Todos con existencia",
};
const ESTADO_CONTEO = { 0: "CANCELADO", 1: "ACTIVO", 2: "EN REVISIÓN", 3: "FINALIZADO" };
const ESTADO_ORDEN  = { 0: "Cancelada", 1: "Pendiente", 2: "Contada", 3: "Aprobada" };

const C_PRIMARY   = "#00BFD6";
const C_DARK      = "#005f6b";
const C_ALT       = "#f0fbfc";
const C_RED       = "#e53935";
const C_GREEN     = "#2e7d32";
const C_ORANGE    = "#e65100";
const C_GRAY_HDR  = "#37474f";
const C_GRAY_LITE = "#eceff1";

// ── Helpers ────────────────────────────────────────────────────────────────
const seccion = (titulo) => ({
    table: {
        widths: ["100%"],
        body: [[{
            text: titulo.toUpperCase(),
            bold: true, fontSize: 8,
            color: "#ffffff",
            fillColor: C_DARK,
            border: [false, false, false, false],
            margin: [6, 4, 6, 4],
        }]],
    },
    margin: [0, 10, 0, 4],
});

const separador = () => ({
    canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: "#b0bec5" }],
    margin: [0, 2, 0, 2],
});

const filaInfo = (label, valor) => [
    { text: label, style: "labelInfo", border: [false, false, false, true] },
    { text: valor || "—", style: "valorInfo", border: [false, false, false, true] },
];

// ── Recomendaciones automáticas ────────────────────────────────────────────
function generarRecomendaciones(conteo, ordenes, diferencias, resumenDif) {
    const recs = [];
    const total    = ordenes.length;
    const aprobadas = ordenes.filter((o) => o.aprobada || o.estado === 3).length;
    const tasa     = total > 0 ? (aprobadas / total) * 100 : 0;

    // Estado general del conteo
    if (conteo?.estado === 0) {
        recs.push({ nivel: "ALERTA", texto: "El conteo fue cancelado. Determine los motivos antes de crear uno nuevo y asegúrese de comunicar la situación al equipo." });
    } else if (conteo?.estado === 1 || conteo?.estado === 2) {
        if (tasa === 0) {
            recs.push({ nivel: "INFO", texto: "El conteo acaba de iniciarse. Defina fechas límite para cada responsable y programe una reunión de arranque para aclarar el proceso." });
        } else if (tasa < 50) {
            recs.push({ nivel: "ATENCIÓN", texto: `Avance del ${tasa.toFixed(0)}% de aprobación. Realice seguimiento diario con los responsables que tienen órdenes pendientes para evitar retrasos.` });
        } else if (tasa < 100) {
            recs.push({ nivel: "INFO", texto: `Buen avance: ${tasa.toFixed(0)}% aprobado. Contacte a los responsables con órdenes incompletas para cerrar el ciclo en el plazo establecido.` });
        }

        const conBajoAvance = ordenes.filter(
            (o) => o.totalInsumos > 0 && (o.insumosContados ?? 0) / o.totalInsumos < 0.5 && o.estado < 3
        );
        if (conBajoAvance.length > 0) {
            recs.push({ nivel: "ATENCIÓN", texto: `${conBajoAvance.length} orden(es) con avance inferior al 50%. Verifique acceso al sistema y disponibilidad física de los insumos asignados.` });
        }
    } else if (conteo?.estado === 3) {
        recs.push({ nivel: "OK", texto: "El conteo ha concluido satisfactoriamente. Revise el análisis de diferencias para tomar acciones correctivas si aplica." });
    }

    // Diferencias
    if (resumenDif) {
        const { totalInsumos, insumosConDiferencia } = resumenDif;
        const tasaDif = totalInsumos > 0 ? (insumosConDiferencia / totalInsumos) * 100 : 0;
        if (tasaDif === 0) {
            recs.push({ nivel: "OK", texto: "Inventario sin diferencias. Excelente nivel de exactitud. Mantenga la periodicidad del conteo para conservar este resultado." });
        } else if (tasaDif <= 5) {
            recs.push({ nivel: "INFO", texto: `Diferencias bajas (${tasaDif.toFixed(1)}% de insumos). Nivel aceptable. Aplique ajustes pendientes y monitoree los insumos afectados en el siguiente ciclo.` });
        } else if (tasaDif <= 20) {
            recs.push({ nivel: "ATENCIÓN", texto: `Diferencias moderadas (${tasaDif.toFixed(1)}% de insumos). Revise los procesos de entrada/salida de inventario, especialmente para los productos con mayor discrepancia.` });
        } else {
            recs.push({ nivel: "ALERTA", texto: `Diferencias altas (${tasaDif.toFixed(1)}% de insumos). Se requiere acción urgente: audite los movimientos de inventario, revise si hay mermas no registradas y considere reducir el periodo entre conteos.` });
        }
    }

    // Ajustes pendientes
    if (diferencias?.length > 0) {
        const pendientes = diferencias.filter((d) => !d.ajusteAplicado && d.diferenciaFinal !== 0).length;
        if (pendientes > 0) {
            recs.push({ nivel: "ATENCIÓN", texto: `${pendientes} ajuste(s) de inventario sin aplicar. Aplíquelos a la brevedad para que el sistema refleje los valores reales del conteo.` });
        } else if (diferencias.every((d) => d.ajusteAplicado)) {
            recs.push({ nivel: "OK", texto: "Todos los ajustes han sido aplicados. El sistema ya refleja los valores corregidos por este conteo." });
        }

        const top = diferencias
            .filter((d) => d.diferenciaFinal !== 0)
            .sort((a, b) => Math.abs(b.diferenciaFinal) - Math.abs(a.diferenciaFinal))
            .slice(0, 3);
        if (top.length > 0) {
            recs.push({ nivel: "INFO", texto: `Insumos con mayores diferencias: ${top.map((d) => d.nombre).join("; ")}. Analice si existen causas recurrentes como errores de captura, robos o daños no reportados.` });
        }

        const sobrantes = diferencias.filter((d) => (d.diferenciaFinal ?? 0) > 0).length;
        const faltantes = diferencias.filter((d) => (d.diferenciaFinal ?? 0) < 0).length;
        if (faltantes > sobrantes && faltantes > 2) {
            recs.push({ nivel: "ALERTA", texto: `Predominan los faltantes (${faltantes} insumos). Evalúe controles de acceso al almacén, posibles mermas no documentadas o errores sistemáticos en la recepción de mercancía.` });
        } else if (sobrantes > faltantes && sobrantes > 2) {
            recs.push({ nivel: "INFO", texto: `Predominan los sobrantes (${sobrantes} insumos). Verifique si hay entradas duplicadas o mercancía recibida sin procesar en el sistema.` });
        }
    }

    if (recs.length === 0) {
        recs.push({ nivel: "INFO", texto: "Continúe el proceso de conteo según el calendario establecido. Una vez finalizado, revise las diferencias para tomar decisiones de ajuste de inventario." });
    }

    return recs;
}

// ── PDF principal ──────────────────────────────────────────────────────────
const conteoDetallePdf = (data) => {
    const { conteo = {}, ordenes = [], diferencias = [], resumenDif = null, resumenReconteos = null } = data;

    const totalOrdenes = ordenes.length;
    const contadas   = ordenes.filter((o) => o.estado >= 2).length;
    const aprobadas  = ordenes.filter((o) => o.aprobada || o.estado === 3).length;
    const pendientes = ordenes.filter((o) => o.estado === 1).length; // eslint-disable-line no-unused-vars
    const progreso   = totalOrdenes > 0 ? Math.round((aprobadas / totalOrdenes) * 100) : 0;

    const hasReconteos = !!resumenReconteos;
    const difSec  = 5 + (hasReconteos ? 1 : 0);
    const recSec  = difSec + (resumenDif ? 1 : 0);

    const ahora     = moment().format("DD [de] MMMM [de] YYYY, HH:mm");
    const estadoLabel = ESTADO_CONTEO[conteo.estado] ?? "—";
    const estadoColor = conteo.estado === 3 ? C_GREEN : conteo.estado === 0 ? C_RED : C_ORANGE;

    // Filas de órdenes
    const filasOrdenes = ordenes.map((o, idx) => {
        const durMin = o.fechaInicio && o.fechaTermino
            ? moment(o.fechaTermino).diff(moment(o.fechaInicio), "minutes") : null;
        const durTexto = durMin !== null
            ? (durMin >= 60 ? `${Math.floor(durMin / 60)}h ${durMin % 60}m` : `${durMin} min`) : "—";
        const pct = o.totalInsumos > 0 ? Math.round(((o.insumosContados ?? 0) / o.totalInsumos) * 100) : 0;
        return [
            { text: o.serieNumero || "—", style: "tCelda", alignment: "center",
              fillColor: idx % 2 === 0 ? C_ALT : "#ffffff" },
            { text: o.responsableNombre || "—", style: "tCelda",
              fillColor: idx % 2 === 0 ? C_ALT : "#ffffff" },
            { text: o.supervisorNombre || "—", style: "tCelda",
              fillColor: idx % 2 === 0 ? C_ALT : "#ffffff" },
            { text: `${o.insumosContados ?? 0} / ${o.totalInsumos ?? 0} (${pct}%)`,
              style: "tCelda", alignment: "center",
              fillColor: idx % 2 === 0 ? C_ALT : "#ffffff" },
            { text: ESTADO_ORDEN[o.estado] || "—", style: "tCelda", alignment: "center",
              color: o.estado === 3 ? C_GREEN : o.estado === 0 ? C_RED : C_GRAY_HDR,
              bold: o.estado === 3,
              fillColor: idx % 2 === 0 ? C_ALT : "#ffffff" },
            { text: o.fechaInicio ? moment(o.fechaInicio).format("DD/MM/YY HH:mm") : "—",
              style: "tCelda", alignment: "center",
              fillColor: idx % 2 === 0 ? C_ALT : "#ffffff" },
            { text: durTexto, style: "tCelda", alignment: "center",
              fillColor: idx % 2 === 0 ? C_ALT : "#ffffff" },
        ];
    });

    // La columna de codigo de barras solo se imprime si algun insumo del conjunto lo trae
    // capturado, para no gastar ancho de pagina en una columna de puros guiones.
    const mostrarCbDif = diferencias.some((d) => !!d.codigoBarras);
    const mostrarCbReconteos = (resumenReconteos?.masReconteados || []).some((i) => !!i.codigoBarras);

    // Filas de diferencias
    const filasDif = diferencias
        .sort((a, b) => Math.abs(b.diferenciaFinal ?? 0) - Math.abs(a.diferenciaFinal ?? 0))
        .map((d, idx) => {
            const diff = d.diferenciaFinal;
            const pct = d.snapshotFinal && d.snapshotFinal !== 0 && diff != null
                ? ((diff / d.snapshotFinal) * 100).toFixed(1) : null;
            const diffColor = diff === 0 ? C_GREEN : diff > 0 ? "#1565c0" : C_RED;
            const fill = idx % 2 === 0 ? C_ALT : "#ffffff";
            return [
                { text: d.nombre || "—", style: "tCelda", fillColor: fill },
                ...(mostrarCbDif
                    ? [{ text: d.codigoBarras || "—", style: "tCelda", fillColor: fill }]
                    : []),
                { text: d.snapshotFinal ?? "—", style: "tCelda", alignment: "right", fillColor: fill },
                { text: d.cantidadFinal ?? "—", style: "tCelda", alignment: "right", fillColor: fill },
                { text: diff != null ? (diff > 0 ? `+${diff}` : String(diff)) : "—",
                  style: "tCelda", alignment: "right", bold: diff !== 0, color: diffColor, fillColor: fill },
                { text: pct !== null ? `${parseFloat(pct) > 0 ? "+" : ""}${pct}%` : "—",
                  style: "tCelda", alignment: "right", color: diffColor, fillColor: fill },
                { text: d.ajusteAplicado ? "✔ Aplicado" : "Pendiente", style: "tCelda", alignment: "center",
                  color: d.ajusteAplicado ? C_GREEN : C_ORANGE, fillColor: fill },
            ];
        });

    const recomendaciones = generarRecomendaciones(conteo, ordenes, diferencias, resumenDif);

    const nivelColor = { OK: C_GREEN, INFO: C_PRIMARY, ATENCIÓN: C_ORANGE, ALERTA: C_RED };

    // ── Construcción del documento ────────────────────────────────────────
    return {
        pageSize: "LETTER",
        pageMargins: [30, 50, 30, 40],

        // Encabezado de página
        header: (page, pages) => ({
            table: {
                widths: ["70%", "30%"],
                body: [[
                    {
                        stack: [
                            { text: "INFORME DE CONTEO DE INVENTARIO", fontSize: 9, bold: true, color: "#ffffff" },
                            { text: conteo.nombre || "—", fontSize: 7, color: "#b2ebf2" },
                        ],
                        fillColor: C_DARK, border: [false, false, false, false],
                        margin: [10, 6, 0, 6],
                    },
                    {
                        stack: [
                            { text: estadoLabel, fontSize: 9, bold: true, color: "#ffffff", alignment: "right" },
                            { text: `Pág. ${page} de ${pages}`, fontSize: 7, color: "#b2ebf2", alignment: "right" },
                        ],
                        fillColor: C_DARK, border: [false, false, false, false],
                        margin: [0, 6, 10, 6],
                    },
                ]],
            },
        }),

        // Pie de página
        footer: {
            text: `Generado el ${ahora} · Biwo ERP`,
            alignment: "center", fontSize: 6, color: "#90a4ae", margin: [0, 8, 0, 0],
        },

        content: [
            // ══════════════════════════════════════════════════════
            // PORTADA / TÍTULO
            // ══════════════════════════════════════════════════════
            {
                table: {
                    widths: ["100%"],
                    body: [[{
                        stack: [
                            { text: "INFORME DE CONTEO", fontSize: 18, bold: true, color: "#ffffff", alignment: "center" },
                            { text: "DE INVENTARIO", fontSize: 14, color: "#b2ebf2", alignment: "center", margin: [0, 2, 0, 0] },
                            separador(),
                            { text: conteo.nombre || "—", fontSize: 11, color: "#e0f7fa", alignment: "center", margin: [0, 6, 0, 0] },
                            { text: estadoLabel, fontSize: 10, bold: true, color: "#ffffff",
                              background: estadoColor, alignment: "center", margin: [0, 4, 0, 2] },
                        ],
                        fillColor: C_DARK,
                        border: [false, false, false, false],
                        margin: [0, 14, 0, 14],
                    }]],
                },
                margin: [0, 0, 0, 14],
            },

            // ══════════════════════════════════════════════════════
            // RESUMEN EJECUTIVO
            // ══════════════════════════════════════════════════════
            seccion("1. Resumen ejecutivo"),
            {
                table: {
                    widths: ["25%", "25%", "25%", "25%"],
                    body: [[
                        {
                            stack: [
                                { text: String(totalOrdenes), style: "kpiNum", color: C_PRIMARY },
                                { text: "Órdenes totales", style: "kpiLabel" },
                            ],
                            fillColor: C_GRAY_LITE, alignment: "center", border: [true, true, true, true],
                            margin: [0, 8, 0, 8],
                        },
                        {
                            stack: [
                                { text: String(contadas), style: "kpiNum", color: "#1565c0" },
                                { text: "Contadas", style: "kpiLabel" },
                            ],
                            fillColor: "#e3f2fd", alignment: "center", border: [true, true, true, true],
                            margin: [0, 8, 0, 8],
                        },
                        {
                            stack: [
                                { text: String(aprobadas), style: "kpiNum", color: C_GREEN },
                                { text: "Aprobadas", style: "kpiLabel" },
                            ],
                            fillColor: "#e8f5e9", alignment: "center", border: [true, true, true, true],
                            margin: [0, 8, 0, 8],
                        },
                        {
                            stack: [
                                { text: `${progreso}%`, style: "kpiNum", color: progreso === 100 ? C_GREEN : C_ORANGE },
                                { text: "Avance general", style: "kpiLabel" },
                            ],
                            fillColor: progreso === 100 ? "#e8f5e9" : "#fff3e0",
                            alignment: "center", border: [true, true, true, true],
                            margin: [0, 8, 0, 8],
                        },
                    ]],
                },
                margin: [0, 0, 0, 6],
            },
            ...(resumenDif ? [{
                table: {
                    widths: ["25%", "25%", "25%", "25%"],
                    body: [[
                        {
                            stack: [
                                { text: String(resumenDif.totalInsumos), style: "kpiNum", color: C_GRAY_HDR },
                                { text: "Insumos contados", style: "kpiLabel" },
                            ],
                            fillColor: C_GRAY_LITE, alignment: "center", border: [true, true, true, true],
                            margin: [0, 8, 0, 8],
                        },
                        {
                            stack: [
                                { text: String(resumenDif.insumosSinDiferencia), style: "kpiNum", color: C_GREEN },
                                { text: "Sin diferencia", style: "kpiLabel" },
                            ],
                            fillColor: "#e8f5e9", alignment: "center", border: [true, true, true, true],
                            margin: [0, 8, 0, 8],
                        },
                        {
                            stack: [
                                { text: String(resumenDif.insumosConDiferencia), style: "kpiNum", color: C_RED },
                                { text: "Con diferencia", style: "kpiLabel" },
                            ],
                            fillColor: "#ffebee", alignment: "center", border: [true, true, true, true],
                            margin: [0, 8, 0, 8],
                        },
                        {
                            stack: [
                                {
                                    text: resumenDif.totalInsumos > 0
                                        ? `${((resumenDif.insumosConDiferencia / resumenDif.totalInsumos) * 100).toFixed(1)}%`
                                        : "—",
                                    style: "kpiNum",
                                    color: resumenDif.insumosConDiferencia === 0 ? C_GREEN : C_RED,
                                },
                                { text: "Tasa de error", style: "kpiLabel" },
                            ],
                            fillColor: resumenDif.insumosConDiferencia === 0 ? "#e8f5e9" : "#ffebee",
                            alignment: "center", border: [true, true, true, true],
                            margin: [0, 8, 0, 8],
                        },
                    ]],
                },
                margin: [0, 0, 0, 0],
            }] : []),

            // ══════════════════════════════════════════════════════
            // DATOS DE CONFIGURACIÓN
            // ══════════════════════════════════════════════════════
            seccion("2. Datos del conteo"),
            {
                table: {
                    widths: ["22%", "28%", "22%", "28%"],
                    body: [
                        [
                            ...filaInfo("Nombre", conteo.nombre),
                            ...filaInfo("Estado", ESTADO_CONTEO[conteo.estado] ?? "—"),
                        ],
                        [
                            ...filaInfo("Modo de conteo", MODO_LABELS[conteo.modoConteo] ?? "—"),
                            ...filaInfo("Estrategia", ESTRATEGIA_LABELS[conteo.estrategiaSeleccionInsumos] ?? "—"),
                        ],
                        [
                            ...filaInfo("Proyecto", conteo.proyectoNombre),
                            ...filaInfo("Supervisor", conteo.supervisorNombre),
                        ],
                        [
                            ...filaInfo("Fecha inicio",
                                conteo.fechaInicio ? moment(conteo.fechaInicio).format("DD/MM/YYYY") : null),
                            ...filaInfo("Fecha vencimiento",
                                conteo.fechaVencimiento ? moment(conteo.fechaVencimiento).format("DD/MM/YYYY") : null),
                        ],
                        ...(conteo.descripcion ? [[
                            { text: "Descripción", style: "labelInfo", border: [false, false, false, true] },
                            { text: conteo.descripcion, style: "valorInfo", colSpan: 3, border: [false, false, false, true] },
                            {}, {},
                        ]] : []),
                    ],
                },
            },

            // ══════════════════════════════════════════════════════
            // DETALLE DE ÓRDENES
            // ══════════════════════════════════════════════════════
            seccion("3. Detalle de órdenes de conteo"),
            {
                table: {
                    widths: ["8%", "19%", "15%", "16%", "12%", "16%", "14%"],
                    body: [
                        [
                            { text: "N°",            style: "tHeader" },
                            { text: "Responsable",   style: "tHeader" },
                            { text: "Supervisor",    style: "tHeader" },
                            { text: "Insumos (%)",   style: "tHeader" },
                            { text: "Estado",        style: "tHeader" },
                            { text: "Inicio",        style: "tHeader" },
                            { text: "Duración",      style: "tHeader" },
                        ],
                        ...(filasOrdenes.length > 0
                            ? filasOrdenes
                            : [[{
                                text: "Sin órdenes registradas.",
                                style: "tCelda", colSpan: 7, alignment: "center",
                                color: "#90a4ae", border: [false, false, false, false],
                              }, {}, {}, {}, {}, {}, {}]]),
                    ],
                },
                layout: {
                    hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? 1 : 0.3,
                    vLineWidth: () => 0.3,
                    hLineColor: () => "#b0bec5",
                    vLineColor: () => "#b0bec5",
                },
            },

            // ══════════════════════════════════════════════════════
            // ESTADÍSTICAS DE AVANCE POR RESPONSABLE
            // ══════════════════════════════════════════════════════
            ...(ordenes.length > 0 ? [
                seccion("4. Rendimiento por responsable"),
                {
                    table: {
                        widths: ["30%", "12%", "12%", "12%", "34%"],
                        body: [
                            [
                                { text: "Responsable",   style: "tHeader" },
                                { text: "Asignados",     style: "tHeader" },
                                { text: "Contados",      style: "tHeader" },
                                { text: "% Avance",      style: "tHeader" },
                                { text: "Estado",        style: "tHeader" },
                            ],
                            ...ordenes.map((o, idx) => {
                                const pct = o.totalInsumos > 0
                                    ? Math.round(((o.insumosContados ?? 0) / o.totalInsumos) * 100) : 0;
                                const fill = idx % 2 === 0 ? C_ALT : "#ffffff";
                                return [
                                    { text: o.responsableNombre || "—", style: "tCelda", fillColor: fill },
                                    { text: String(o.totalInsumos ?? 0), style: "tCelda", alignment: "center", fillColor: fill },
                                    { text: String(o.insumosContados ?? 0), style: "tCelda", alignment: "center", fillColor: fill },
                                    { text: `${pct}%`, style: "tCelda", alignment: "center",
                                      color: pct === 100 ? C_GREEN : pct > 0 ? C_PRIMARY : C_GRAY_HDR,
                                      bold: pct === 100, fillColor: fill },
                                    { text: ESTADO_ORDEN[o.estado] || "—", style: "tCelda",
                                      color: o.estado === 3 ? C_GREEN : o.estado === 0 ? C_RED : C_GRAY_HDR,
                                      fillColor: fill },
                                ];
                            }),
                        ],
                    },
                    layout: {
                        hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? 1 : 0.3,
                        vLineWidth: () => 0.3,
                        hLineColor: () => "#b0bec5",
                        vLineColor: () => "#b0bec5",
                    },
                },
            ] : []),

            // ══════════════════════════════════════════════════════
            // RECONTEOS
            // ══════════════════════════════════════════════════════
            ...(hasReconteos ? [
                seccion("5. Análisis de reconteos"),
                {
                    table: {
                        widths: ["33%", "33%", "34%"],
                        body: [[
                            {
                                stack: [
                                    { text: String(resumenReconteos.totalReconteos ?? 0), style: "kpiNum", color: "#1565c0" },
                                    { text: "Reconteos totales", style: "kpiLabel" },
                                ],
                                fillColor: "#e3f2fd", alignment: "center", border: [true, true, true, true],
                                margin: [0, 8, 0, 8],
                            },
                            {
                                stack: [
                                    { text: String(resumenReconteos.insumosConReconteo ?? 0), style: "kpiNum",
                                      color: (resumenReconteos.insumosConReconteo ?? 0) > 0 ? C_ORANGE : C_GREEN },
                                    { text: "Insumos con reconteo", style: "kpiLabel" },
                                ],
                                fillColor: (resumenReconteos.insumosConReconteo ?? 0) > 0 ? "#fff3e0" : "#e8f5e9",
                                alignment: "center", border: [true, true, true, true],
                                margin: [0, 8, 0, 8],
                            },
                            {
                                stack: [
                                    {
                                        text: (resumenReconteos.totalInsumos ?? 0) > 0
                                            ? `${(((resumenReconteos.insumosConReconteo ?? 0) / resumenReconteos.totalInsumos) * 100).toFixed(1)}%`
                                            : "—",
                                        style: "kpiNum",
                                        color: (resumenReconteos.insumosConReconteo ?? 0) === 0 ? C_GREEN : C_ORANGE,
                                    },
                                    { text: "Tasa de reconteo", style: "kpiLabel" },
                                ],
                                fillColor: (resumenReconteos.insumosConReconteo ?? 0) === 0 ? "#e8f5e9" : "#fff3e0",
                                alignment: "center", border: [true, true, true, true],
                                margin: [0, 8, 0, 8],
                            },
                        ]],
                    },
                    margin: [0, 0, 0, 6],
                },
                ...((resumenReconteos.masReconteados || []).length > 0 ? [
                    { text: "Insumos con más reconteos", bold: true, fontSize: 8, color: C_DARK, margin: [0, 6, 0, 3] },
                    {
                        table: {
                            widths: mostrarCbReconteos
                                ? ["38%", "20%", "18%", "24%"]
                                : ["55%", "20%", "25%"],
                            body: [
                                [
                                    { text: "Insumo",     style: "tHeader" },
                                    ...(mostrarCbReconteos ? [{ text: "Cód. barras", style: "tHeader" }] : []),
                                    { text: "Reconteos",  style: "tHeader" },
                                    { text: "Severidad",  style: "tHeader" },
                                ],
                                ...(resumenReconteos.masReconteados || []).map((item, idx) => {
                                    const sev = item.totalReconteos >= 5
                                        ? { label: "Alta",  color: C_RED }
                                        : item.totalReconteos >= 2
                                            ? { label: "Media", color: C_ORANGE }
                                            : { label: "Baja",  color: C_GREEN };
                                    const fill = idx % 2 === 0 ? C_ALT : "#ffffff";
                                    return [
                                        { text: item.nombre || "—", style: "tCelda", fillColor: fill },
                                        ...(mostrarCbReconteos
                                            ? [{ text: item.codigoBarras || "—", style: "tCelda", fillColor: fill }]
                                            : []),
                                        { text: String(item.totalReconteos ?? 0), style: "tCelda", alignment: "center", fillColor: fill },
                                        { text: sev.label, style: "tCelda", alignment: "center", color: sev.color, bold: true, fillColor: fill },
                                    ];
                                }),
                            ],
                        },
                        layout: {
                            hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? 1 : 0.3,
                            vLineWidth: () => 0.3,
                            hLineColor: () => "#b0bec5",
                            vLineColor: () => "#b0bec5",
                        },
                    },
                ] : []),
                ...((resumenReconteos.porResponsable || []).length > 0 ? [
                    { text: "Reconteos por responsable", bold: true, fontSize: 8, color: C_DARK, margin: [0, 10, 0, 3] },
                    {
                        table: {
                            widths: ["70%", "30%"],
                            body: [
                                [
                                    { text: "Responsable",     style: "tHeader" },
                                    { text: "Total reconteos", style: "tHeader" },
                                ],
                                ...[...resumenReconteos.porResponsable]
                                    .sort((a, b) => (b.totalReconteos ?? 0) - (a.totalReconteos ?? 0))
                                    .map((r, idx) => {
                                        const fill = idx % 2 === 0 ? C_ALT : "#ffffff";
                                        return [
                                            { text: r.responsableNombre || "—", style: "tCelda", fillColor: fill },
                                            { text: String(r.totalReconteos ?? 0), style: "tCelda", alignment: "center",
                                              color: (r.totalReconteos ?? 0) > 0 ? C_ORANGE : C_GREEN, fillColor: fill },
                                        ];
                                    }),
                            ],
                        },
                        layout: {
                            hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? 1 : 0.3,
                            vLineWidth: () => 0.3,
                            hLineColor: () => "#b0bec5",
                            vLineColor: () => "#b0bec5",
                        },
                    },
                ] : []),
            ] : []),

            // ══════════════════════════════════════════════════════
            // ANÁLISIS DE DIFERENCIAS
            // ══════════════════════════════════════════════════════
            ...(resumenDif && filasDif.length > 0 ? [
                seccion(`${difSec}. Análisis de diferencias de inventario`),
                {
                    text: "Tabla ordenada de mayor a menor diferencia absoluta. Los valores positivos indican sobrante y los negativos faltante.",
                    style: "notaPie", margin: [0, 0, 0, 4],
                },
                {
                    table: {
                        widths: mostrarCbDif
                            ? ["24%", "16%", "11%", "11%", "11%", "11%", "16%"]
                            : ["32%", "12%", "12%", "12%", "12%", "20%"],
                        body: [
                            [
                                { text: "Insumo",        style: "tHeader" },
                                ...(mostrarCbDif ? [{ text: "Cód. barras", style: "tHeader" }] : []),
                                { text: "Snapshot",      style: "tHeader" },
                                { text: "Cant. final",   style: "tHeader" },
                                { text: "Diferencia",    style: "tHeader" },
                                { text: "% Error",       style: "tHeader" },
                                { text: "Ajuste",        style: "tHeader" },
                            ],
                            ...filasDif,
                        ],
                    },
                    layout: {
                        hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? 1 : 0.3,
                        vLineWidth: () => 0.3,
                        hLineColor: () => "#b0bec5",
                        vLineColor: () => "#b0bec5",
                    },
                },
                // Resumen ajustes
                (() => {
                    const aplicados = diferencias.filter((d) => d.ajusteAplicado).length;
                    const pend      = diferencias.filter((d) => !d.ajusteAplicado && d.diferenciaFinal !== 0).length;
                    return {
                        table: {
                            widths: ["50%", "50%"],
                            body: [[
                                { text: `Ajustes aplicados: ${aplicados} de ${diferencias.filter((d) => d.diferenciaFinal !== 0).length}`,
                                  style: "tCelda", color: C_GREEN, border: [false, true, false, false], margin: [0, 4, 0, 0] },
                                { text: pend > 0 ? `⚠ ${pend} ajuste(s) pendiente(s) de aplicar` : "✔ Sin ajustes pendientes",
                                  style: "tCelda", color: pend > 0 ? C_ORANGE : C_GREEN,
                                  alignment: "right", border: [false, true, false, false], margin: [0, 4, 0, 0] },
                            ]],
                        },
                        margin: [0, 2, 0, 0],
                    };
                })(),
            ] : (resumenDif && filasDif.length === 0 ? [
                seccion(`${difSec}. Análisis de diferencias`),
                { text: "✔ No se detectaron diferencias entre el conteo y el sistema. Inventario exacto.", style: "tCelda", color: C_GREEN, margin: [0, 4, 0, 4] },
            ] : [])),

            // ══════════════════════════════════════════════════════
            // RECOMENDACIONES
            // ══════════════════════════════════════════════════════
            seccion(`${recSec}. Recomendaciones y acciones sugeridas`),
            ...recomendaciones.map((rec, i) => ({
                table: {
                    widths: ["8%", "92%"],
                    body: [[
                        {
                            text: rec.nivel,
                            fontSize: 6, bold: true,
                            color: "#ffffff",
                            fillColor: nivelColor[rec.nivel] ?? C_PRIMARY,
                            alignment: "center",
                            border: [false, false, false, false],
                            margin: [2, 4, 2, 4],
                        },
                        {
                            text: `${i + 1}. ${rec.texto}`,
                            fontSize: 8, lineHeight: 1.35,
                            fillColor: i % 2 === 0 ? "#fafafa" : "#ffffff",
                            border: [false, false, false, false],
                            margin: [6, 4, 4, 4],
                        },
                    ]],
                },
                margin: [0, 1, 0, 1],
            })),

            // ══════════════════════════════════════════════════════
            // CIERRE / FIRMA
            // ══════════════════════════════════════════════════════
            {
                canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: C_PRIMARY }],
                margin: [0, 18, 0, 10],
            },
            {
                table: {
                    widths: ["50%", "50%"],
                    body: [[
                        {
                            stack: [
                                { text: "Fecha de generación", fontSize: 7, color: "#90a4ae" },
                                { text: ahora, fontSize: 8, bold: true },
                            ],
                            border: [false, false, false, false],
                        },
                        {
                            stack: [
                                { text: "Sistema", fontSize: 7, color: "#90a4ae", alignment: "right" },
                                { text: "Biwo ERP — Módulo de Conteos", fontSize: 8, bold: true, alignment: "right" },
                            ],
                            border: [false, false, false, false],
                        },
                    ]],
                },
            },
            {
                table: {
                    widths: ["34%", "32%", "34%"],
                    body: [[
                        { text: "", border: [false, false, false, false] },
                        {
                            stack: [
                                { canvas: [{ type: "line", x1: 0, y1: 0, x2: 140, y2: 0, lineWidth: 0.5, lineColor: "#455a64" }] },
                                { text: "Responsable de autorizar", fontSize: 7, color: "#90a4ae", alignment: "center", margin: [0, 3, 0, 0] },
                            ],
                            border: [false, false, false, false],
                            margin: [0, 20, 0, 0],
                        },
                        { text: "", border: [false, false, false, false] },
                    ]],
                },
                margin: [0, 10, 0, 0],
            },
        ],

        styles: {
            kpiNum:    { fontSize: 18, bold: true },
            kpiLabel:  { fontSize: 7,  color: "#607d8b", margin: [0, 2, 0, 0] },
            labelInfo: { fontSize: 7,  bold: true, color: "#455a64", fillColor: "#eceff1",
                         margin: [4, 3, 4, 3] },
            valorInfo: { fontSize: 8,  margin: [4, 3, 4, 3] },
            tHeader:   { fontSize: 7,  bold: true, color: "#ffffff", fillColor: C_DARK,
                         alignment: "center", margin: [2, 4, 2, 4] },
            tCelda:    { fontSize: 7,  margin: [3, 3, 3, 3] },
            notaPie:   { fontSize: 6,  italics: true, color: "#90a4ae" },
        },
    };
};

export default conteoDetallePdf;
